import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { PageLoader, ModalComponent, IModalData, PasslyButton } from '@app/components';
import { useTypedTranslation } from '@app/hooks';
import { booleanToYesNo, genericErrorToast, defaultOrgId, hasOrgPermission, hasDefaultOrgAdminPermission, isDefaultOrgUser, isLoggedInToDefaultOrg } from '@app/helpers';
import { ManageLicense, ManageSystem } from '@app/store/constants';
import { IOrganizationDetail, IOrganizationLicenseInfo } from '@app/models';
import { handleGetOrganization, getModalSwitch, addDetailsBreadcrumb, removeDetailsBreadcrumb, setExceptionHandler } from '@app/store/actions';
import moment from 'moment';
import _ from 'lodash';
import CancelIcon from '@material-ui/icons/Cancel';
import { ClearOrgLogoDialog } from '@app/pages/directoryManager/dialogs/clearOrganizationImage.dialog';
import { DeleteOrganizationDialog } from '@app/pages/directoryManager/dialogs/deleteOrganization.dialog';
import { EditOrganizationDialog } from '@app/pages/directoryManager/dialogs/editOrganization.dialog';
import { EditOrgPasswordPolicyDialog } from '@app/pages/directoryManager/dialogs/editOrgPasswordPolicy.dialog';
import { licenseAllocationService, basicService, organizationsService } from '@app/services';
import { AxiosResponse } from 'axios';
import { confirm } from '@app/components/Modal/confirmModal';
import { handleSetSnackbar } from '@app/store/actions/snackbar.actions';

const OrganizationDetailsPage = (props: any) => {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const thisOrganization: IOrganizationDetail = useSelector((state: any) => state.orgs.organization);
    const homeRealmSuffix: string = useSelector((state: any) => state.basic.shellConfig.configs.homeRealmSuffix);
    const loadingData: any = useSelector((state: any) => state.basic.loadingData);
    const modalSwitchState: boolean = useSelector((state: any) => state.modal);

    const [modalData, setModalData] = useState<IModalData>({
        headerTitle: '',
        modalSwitcher: modalSwitchState
    });
    const [licenseData, setLicenseData] = useState<IOrganizationLicenseInfo>();

    const organizationId: string = props.match.params.id;

    useEffect(() => {
        dispatch(setExceptionHandler(true));
        dispatch(handleGetOrganization(organizationId));

        return () => {
            dispatch(removeDetailsBreadcrumb());
        };
    }, []);

    useEffect(() => {
        dispatch(addDetailsBreadcrumb(thisOrganization.Name));
    }, [thisOrganization]);

    useEffect(() => {
        handleGetLicenseData();
    }, [thisOrganization, organizationId]);

    const editOrganization = () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('EDIT_ORG'),
            modalSwitcher: modalSwitchState,
            form: <EditOrganizationDialog orgId={thisOrganization.Id} orgName={thisOrganization.Name} principalNameSuffix={thisOrganization.PrincipalNameSuffix}
                authPolicyId={thisOrganization.DefaultPolicyId} handleRefresh={handlePageRefreshFromDialog} imageUrl={computeOrgBrandingImageUrl(thisOrganization)} />
        });
    };

    const handlePageRefreshFromDialog = (refreshPage: boolean) => {
        if (refreshPage) {
            dispatch(handleGetOrganization(organizationId));
        }
    };

    const editPasswordPolicy = () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('EDIT_PASS_POLICY'),
            modalSwitcher: modalSwitchState,
            form: <EditOrgPasswordPolicyDialog orgId={thisOrganization.Id} lockAfterAttempts={thisOrganization.PasswordPolicy.LockAfterAttempts}
                lockoutDuration={thisOrganization.PasswordPolicy.LockoutDuration} resetRequiresVerification={thisOrganization.PasswordPolicy.ResetRequiresVerification}
                resetByEmail={thisOrganization.PasswordPolicy.ResetByEmail} resetBySms={thisOrganization.PasswordPolicy.ResetBySms}
                requiresComplexity={thisOrganization.PasswordPolicy.RequiresComplexity} minimumPasswordAge={thisOrganization.PasswordPolicy.MinimumPasswordAge}
                maximumPasswordAge={thisOrganization.PasswordPolicy.MaximumPasswordAge} minimumPasswordLength={thisOrganization.PasswordPolicy.MinimumPasswordLength}
                handleRefresh={handlePageRefreshFromDialog} />
        });
    };

    const getPolicyTime = (val: any) => {
        if (!val) {
            return null;
        }

        let dur: any = moment.duration(val);

        if (dur._days > 36500) {
            return translation('DATE_NEVER');
        }

        return dur.humanize();
    };

    const computeOrgBrandingImageUrl = (org: IOrganizationDetail) => {
        if (org && org.Settings && org.Settings.length) {
            let orgBrandingImageUrlSetting = _.find(org.Settings, { 'Name': 'Org.Branding.ImageUrl' });
            if (orgBrandingImageUrlSetting) {
                return orgBrandingImageUrlSetting.Value;
            }
        }
        return '';
    };

    const clearOrgImageDialog = () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('ORG_CLEAR_LOGO_CONFIRM_HEADER'),
            modalSwitcher: modalSwitchState,
            form: <ClearOrgLogoDialog />
        });
    };

    const deleteOrganizationDialog = () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('DELETE_ORGANIZATION'),
            modalSwitcher: modalSwitchState,
            form: <DeleteOrganizationDialog />
        });
    };

    const handleGetLicenseData = () => {
        licenseAllocationService.getOrgLicense(organizationId, genericErrorToast, handleGetLicenseDataSuccess);
    };

    const handleGetLicenseDataSuccess = (response: AxiosResponse<IOrganizationLicenseInfo>) => {
        setLicenseData(response.data);
    };

    const forceSignoutDialog = () => {
        confirm(translation('FORCE_SIGNOUT_ORG_CONFIRM'), translation('SIGNOUT'), translation('NO'), translation('CONFIRM'), false, {
            isDeleteAction: true
        }).then(
            () => {
                organizationsService.forceSignout(organizationId)
                    .then(() => {
                        dispatch(handleSetSnackbar({ show: true, message: translation('ORG_FORCE_SIGNED_OUT'), severity: 'success' }));
                    })
                    .catch(() => {
                        dispatch(handleSetSnackbar({ show: true, message: translation('GENERIC_ERROR'), severity: 'error' }))
                    });
            },
            () => {
                // on cancel
            }
        );
    };

    return (
        <>
            <ModalComponent {...modalData} />
            {loadingData && <PageLoader />}
            {!loadingData && thisOrganization.DefaultPolicy !== undefined &&
                <>
                    <div className='content--narrow detailPage'>
                        <div className='detailPage__section'>
                            <div className='detailPage__section-head'>
                                {translation('ORGANIZATION_DETAILS')}
                            </div>
                            <div className='detailPage__section-content'>
                                <ul className='detailPage__list'>

                                    <li className='detailPage__list-subhead'>
                                        <span className='detailPage__list--primary-label detailPage__section-subhead'>{translation('BASIC_INFORMATION')}</span>
                                        <div className='detailPage__section-content'>
                                            {hasDefaultOrgAdminPermission() && thisOrganization.Id !== defaultOrgId &&
                                                <PasslyButton id='deleteOrgButton' className='modal__actions--primary' variant='contained' color='primary' onClick={deleteOrganizationDialog}>
                                                    {translation('DELETE_ORGANIZATION')}
                                                </PasslyButton>
                                            }
                                            {hasDefaultOrgAdminPermission() && isDefaultOrgUser() && isLoggedInToDefaultOrg() &&
                                                <span style={{marginLeft: 15}}>
                                                    <PasslyButton id='forceSignoutOrganizationButton' className='modal__actions--primary' variant='contained' color='primary' onClick={forceSignoutDialog}>
                                                        {translation('SIGNOUT').toUpperCase()}
                                                    </PasslyButton>
                                                </span>
                                            }
                                        </div>
                                    </li>
                                    {hasOrgPermission([ManageSystem]) &&
                                        <li>
                                            <a id='editOrgLink' className='detailPage__list--primary-label' onClick={() => editOrganization()}>{translation('EDIT')}</a>
                                        </li>
                                    }
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('ORG_NAME')}</span>
                                        <span className='detailPage__list--secondary-label'>{thisOrganization.Name}</span>
                                    </li>
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('ORG_HOMEREALM')}</span>
                                        <span className='detailPage__list--secondary-label'>{`https://${thisOrganization.HomeRealm}.${homeRealmSuffix}`}</span>
                                    </li>
                                    {thisOrganization.PrincipalNameSuffix &&
                                        <li>
                                            <span className='detailPage__list--primary-label'>{translation('ORG_SUFFIX')}</span>
                                            <span className='detailPage__list--secondary-label'>{thisOrganization.PrincipalNameSuffix}</span>
                                        </li>
                                    }
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('ORG_POLICY')}</span>
                                        <span className='detailPage__list--secondary-label'>{thisOrganization.DefaultPolicy.DisplayName}</span>
                                    </li>
                                    <li>
                                        <span className='detailPage__list--primary-label detailPage__list--primary-label-image'>{translation('ORG_BRAND_LOGO')}</span>
                                        {
                                            computeOrgBrandingImageUrl(thisOrganization) &&
                                            <span className='detailPage__list--secondary-label detailPage__list--secondary-label-image'>
                                                <img className='organization-detail__logo' src={computeOrgBrandingImageUrl(thisOrganization)} alt='img' />
                                                <PasslyButton id='clearOrgImage' onClick={clearOrgImageDialog}><CancelIcon className='detailPage__list--secondary-label-close' /></PasslyButton>
                                            </span> ||
                                            <span className='detailPage__list--secondary-label detailPage__list--secondary-label-inactive'>{translation('ORG_BRAND_IMAGE_NOT_SET')}</span>
                                        }
                                    </li>

                                    {hasOrgPermission([ManageLicense]) && isDefaultOrgUser() &&
                                        <>
                                            <li>
                                                <a id='editOrgLicenseLink' className='detailPage__list--primary-label' onClick={() => basicService.redirect('/license/allocation')}>{translation('ORGANIZATION_LICENSE_EDIT_LINK')}</a>
                                            </li>
                                            {licenseData?.LicenseEdition && <>
                                                <li>
                                                    <span className='detailPage__list--primary-label'>{translation('LICENSE_EDITION')}</span>
                                                    <span className='detailPage__list--secondary-label'>{licenseData.LicenseEdition}</span>
                                                </li>
                                                <li>
                                                    <span className='detailPage__list--primary-label'>{translation('LICENSE_SEATS')}</span>
                                                    <span className='detailPage__list--secondary-label'>{licenseData.Seats}</span>
                                                </li>
                                                <li>
                                                    <span className='detailPage__list--primary-label'>{translation('LICENSE_ACTIVE')}</span>
                                                    <span className='detailPage__list--secondary-label'>{licenseData.Active}</span>
                                                </li>
                                            </>}
                                        </>}
                                    <li>
                                        <span className='detailPage__list--primary-label detailPage__section-subhead'>{translation('ORG_PASS_POLICY')}</span>
                                    </li>
                                    {hasOrgPermission([ManageSystem]) &&
                                        <li>
                                            <a id='editPasswordPolicyLink' className='detailPage__list--primary-label' onClick={() => editPasswordPolicy()}>{translation('EDIT')}</a>
                                        </li>
                                    }
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('LOCK_AFTER_ATTEMPTS')}</span>
                                        <span className='detailPage__list--secondary-label'>{thisOrganization.PasswordPolicy.LockAfterAttempts}{' '}
                                            {(thisOrganization.PasswordPolicy.LockAfterAttempts === 1 && translation('LOCK_ATTEMPTS_SINGLE')) ||
                                                translation('LOCK_ATTEMPTS_PLURAL')}
                                        </span>
                                    </li>
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('LOCKOUT_DURATION')}</span>
                                        <span className='detailPage__list--secondary-label detailPage__list--secondary-label-gap'>{getPolicyTime(thisOrganization.PasswordPolicy.LockoutDuration)}</span>
                                    </li>
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('REQUIRE_RESET_CODE')}</span>
                                        <span className='detailPage__list--secondary-label'>{translation(booleanToYesNo(thisOrganization.PasswordPolicy.ResetRequiresVerification))}</span>
                                    </li>
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('RESET_BY_EMAIL')}</span>
                                        <span className='detailPage__list--secondary-label'>{translation(booleanToYesNo(thisOrganization.PasswordPolicy.ResetByEmail))}</span>
                                    </li>
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('RESET_BY_SMS')}</span>
                                        <span className='detailPage__list--secondary-label detailPage__list--secondary-label-gap'>{translation(booleanToYesNo(thisOrganization.PasswordPolicy.ResetBySms))}</span>
                                    </li>
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('PASS_REQUIRES_COMPLEX')}</span>
                                        <span className='detailPage__list--secondary-label'>{translation(booleanToYesNo(thisOrganization.PasswordPolicy.RequiresComplexity))}</span>
                                    </li>
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('AD_PASS_MIN_AGE')}</span>
                                        <span className='detailPage__list--secondary-label'>{getPolicyTime(thisOrganization.PasswordPolicy.MinimumPasswordAge)}</span>
                                    </li>
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('AD_PASS_MAX_AGE')}</span>
                                        <span className='detailPage__list--secondary-label'>{getPolicyTime(thisOrganization.PasswordPolicy.MaximumPasswordAge)}</span>
                                    </li>
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('AD_PASS_MIN_LENGTH')}</span>
                                        <span className='detailPage__list--secondary-label'>{thisOrganization.PasswordPolicy.MinimumPasswordLength}</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </>
            }
        </>
    );
};

export default OrganizationDetailsPage;